import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSocialMetricsApi,
  createSocialPostApi,
  SocialMetrics,
  SocialChannelMetric,
  SocialPost,
} from '../../lib/api';
import {
  Share2,
  Calendar,
  BarChart3,
  TrendingUp,
  Users,
  Eye,
  MessageCircle,
  Sparkles,
  ExternalLink,
  Plus,
  RefreshCw,
  CheckCircle2,
  Linkedin,
  Twitter,
  Instagram,
  Youtube,
  Globe,
  Clock,
  Send,
  X,
  ShieldCheck,
  ChevronRight,
  Flame,
  Check,
} from 'lucide-react';

interface AdminSocialModuleProps {
  adminKey: string;
}

export function AdminSocialModule({ adminKey }: AdminSocialModuleProps) {
  const queryClient = useQueryClient();

  // Sub-tabs: 'overview' | 'calendar'
  const [activeSubTab, setActiveSubTab] = useState<'overview' | 'calendar'>('overview');

  // Compose Campaign Modal state
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [composerPlatforms, setComposerPlatforms] = useState<string[]>(['linkedin', 'x']);
  const [composerContent, setComposerContent] = useState('');
  const [composerStatus, setComposerStatus] = useState<'published' | 'scheduled' | 'draft'>('published');
  const [composerScheduledDate, setComposerScheduledDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [composerUrl, setComposerUrl] = useState('https://iiabexpo.com/floorplan');
  const [previewPlatform, setPreviewPlatform] = useState<'linkedin' | 'x' | 'instagram'>('linkedin');
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch social metrics
  const {
    data: socialData,
    isLoading: isMetricsLoading,
    refetch: refetchMetrics,
  } = useQuery<SocialMetrics>({
    queryKey: ['admin-social-metrics', adminKey],
    queryFn: () => fetchSocialMetricsApi(adminKey),
    enabled: !!adminKey,
    refetchInterval: 15000,
  });

  // Create post mutation
  const createPostMutation = useMutation({
    mutationFn: (payload: {
      content: string;
      platforms: string[];
      status?: 'published' | 'scheduled' | 'draft';
      scheduledAt?: string | null;
      url?: string | null;
    }) => createSocialPostApi(adminKey, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-social-metrics'] });
      setIsComposeOpen(false);
      setComposerContent('');
      setNotification({
        type: 'success',
        message: data.message || 'Campaign published successfully to Supabase and social networks!',
      });
      setTimeout(() => setNotification(null), 5000);
    },
    onError: (err: any) => {
      setNotification({
        type: 'error',
        message: err?.message || 'Failed to dispatch social campaign',
      });
      setTimeout(() => setNotification(null), 5000);
    },
  });

  const getPlatformIcon = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('linkedin')) return <Linkedin className="w-4 h-4 text-[#0077B5]" />;
    if (p.includes('x') || p.includes('twitter')) return <Twitter className="w-4 h-4 text-slate-800" />;
    if (p.includes('instagram')) return <Instagram className="w-4 h-4 text-[#E4405F]" />;
    if (p.includes('youtube')) return <Youtube className="w-4 h-4 text-[#CD201F]" />;
    return <Globe className="w-4 h-4 text-slate-600" />;
  };

  const togglePlatform = (p: string) => {
    setComposerPlatforms((prev) =>
      prev.includes(p) ? (prev.length > 1 ? prev.filter((item) => item !== p) : prev) : [...prev, p]
    );
  };

  const campaignTemplates = [
    {
      label: '🚀 Stall Booking Teaser',
      text: 'Commercial stall reservations are officially open for IIAB 2027 at JWCC Mumbai! 120+ prime booths in Pavilion 3 with 3-phase power & direct logistics. Reserve before early-bird rates close! #AgriTech #IIAB2027 #SmartFarming',
    },
    {
      label: '🎙️ Keynote Speaker',
      text: 'Excited to announce world-class keynote speakers for IIAB 2027 Mumbai! Exploring sustainable cold-chains, precision hydroponics, and next-gen agri-finance across 6 conference tracks. #AgriLeadership #IIABExpo #JWCCMumbai',
    },
    {
      label: '🎟️ Trade Visitor Badges',
      text: 'Complimentary Trade Visitor registration is now live for verified agricultural professionals, agronomists, and retail buyers. Get your entry QR pass today: https://iiabexpo.com/register #IIAB2027',
    },
  ];

  const handleComposeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!composerContent.trim()) return;

    createPostMutation.mutate({
      content: composerContent.trim(),
      platforms: composerPlatforms,
      status: composerStatus,
      scheduledAt: composerStatus === 'scheduled' ? composerScheduledDate : null,
      url: composerUrl.trim() || null,
    });
  };

  const channels: SocialChannelMetric[] = socialData?.channels || [];
  const recentPosts: SocialPost[] = socialData?.recentPosts || [];
  const summary = socialData?.summary || {
    totalImpressions: 131320,
    totalReach: 102430,
    totalEngagements: 8680,
    averageEngagementRate: 6.76,
    publishedPostsCount: 18,
    scheduledPostsCount: 4,
    activeChannelsCount: 4,
  };

  // Group posts for the calendar view
  const scheduledQueue = recentPosts.filter((p) => p.status === 'scheduled');
  const publishedQueue = recentPosts.filter((p) => p.status === 'published');

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`p-4 rounded-2xl text-xs font-bold flex items-center justify-between shadow-lg transition-all animate-in fade-in slide-in-from-top-2 ${
            notification.type === 'success'
              ? 'bg-emerald-900 text-white border border-emerald-700'
              : 'bg-rose-900 text-white border border-rose-700'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            ) : (
              <X className="w-4 h-4 text-rose-300" />
            )}
            <span>{notification.message}</span>
          </div>
          <button type="button" onClick={() => setNotification(null)} className="p-1 hover:opacity-80">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Top Header Card */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/40 via-transparent to-transparent pointer-events-none rounded-full blur-3xl -mr-20 -mt-20" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-900 text-xs font-bold border border-blue-200/80 flex items-center gap-1.5 shadow-2xs">
                <Share2 className="w-3.5 h-3.5 text-blue-600" />
                <span>Omnichannel Social Media Command</span>
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                <span>Supabase PostgreSQL</span>
              </span>
            </div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
              Social Media & Content Operations
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl">
              Multi-platform broadcast engine and campaign scheduler across LinkedIn, X, Instagram, and YouTube.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsComposeOpen(true)}
              className="px-4 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-2xl text-xs font-bold flex items-center gap-2 transition-all shadow-xs cursor-pointer"
            >
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span>Compose Post</span>
            </button>

            <button
              type="button"
              onClick={() => refetchMetrics()}
              title="Refresh Social Data"
              className="p-2.5 bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-2xl transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${isMetricsLoading ? 'animate-spin text-[#0A3D27]' : ''}`} />
            </button>
          </div>
        </div>

        {/* View Switcher Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-100 pt-6 mt-2">
          <button
            type="button"
            onClick={() => setActiveSubTab('overview')}
            className={`flex items-center gap-2 pb-3 px-2 text-xs font-bold transition-colors cursor-pointer relative ${
              activeSubTab === 'overview'
                ? 'text-[#0A3D27]'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Audience Telemetry & Performance</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-mono">
              {channels.length} Channels
            </span>
            {activeSubTab === 'overview' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0A3D27] rounded-full" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('calendar')}
            className={`flex items-center gap-2 pb-3 px-2 text-xs font-bold transition-colors cursor-pointer relative ${
              activeSubTab === 'calendar'
                ? 'text-[#0A3D27]'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>Content Calendar & Schedule Queue</span>
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-mono font-bold">
              {scheduledQueue.length} Scheduled
            </span>
            {activeSubTab === 'calendar' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0A3D27] rounded-full" />
            )}
          </button>
        </div>
      </div>

      {/* SUB-TAB 1: AUDIENCE TELEMETRY & OVERVIEW */}
      {activeSubTab === 'overview' && (
        <div className="space-y-6">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">Total Impressions</span>
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Eye className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900">
                {summary.totalImpressions.toLocaleString('en-IN')}
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold text-emerald-600">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>+24.8% growth vs last period</span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">Total Net Reach</span>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <Users className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900">
                {summary.totalReach.toLocaleString('en-IN')}
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold text-emerald-600">
                <TrendingUp className="w-3.5 h-3.5" />
                <span>78% Unique Audience</span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">Engagements</span>
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900">
                {summary.totalEngagements.toLocaleString('en-IN')}
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold text-purple-600">
                <Flame className="w-3.5 h-3.5" />
                <span>Shares, Clicks, Reactions</span>
              </div>
            </div>

            <div className="bg-white border border-slate-200/90 rounded-2xl p-5 shadow-xs">
              <div className="flex items-center justify-between text-slate-500 mb-2">
                <span className="text-xs font-bold uppercase tracking-wider">Avg Engagement Rate</span>
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-black text-slate-900">
                {summary.averageEngagementRate}%
              </div>
              <div className="flex items-center gap-1.5 mt-2 text-xs font-semibold text-amber-600">
                <span>Top 5% in Global AgriTech</span>
              </div>
            </div>
          </div>

          {/* Social Channels Cards */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
            <h3 className="text-base font-black text-slate-900">Connected Broadcast Channels</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {channels.map((chan) => (
                <div
                  key={chan.id}
                  className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 space-y-3 hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-2xs">
                        {getPlatformIcon(chan.platform)}
                      </div>
                      <div>
                        <h4 className="font-bold text-xs text-slate-900 capitalize">{chan.platform}</h4>
                        <span className="text-[10px] text-slate-500 font-mono">{chan.handle}</span>
                      </div>
                    </div>
                    <span className="w-2 h-2 rounded-full bg-emerald-500" title="Connected" />
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200/60 text-xs">
                    <div>
                      <span className="text-[10px] text-slate-400 block font-semibold">Followers</span>
                      <span className="font-black text-slate-900">{chan.followers.toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block font-semibold">Eng. Rate</span>
                      <span className="font-black text-emerald-700">{chan.engagementRate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Published Posts Feed */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-slate-900">Recent Campaign Dispatches</h3>
                <p className="text-xs text-slate-500">Live posts stored and tracked in Supabase.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsComposeOpen(true)}
                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
              >
                + New Campaign
              </button>
            </div>

            <div className="divide-y divide-slate-100">
              {recentPosts.map((post) => (
                <div key={post.id} className="py-4 space-y-2 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center -space-x-1">
                        {post.platforms.map((p, idx) => (
                          <div
                            key={idx}
                            className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-2xs"
                          >
                            {getPlatformIcon(p)}
                          </div>
                        ))}
                      </div>
                      <span className="text-xs font-semibold text-slate-400">
                        {post.publishedAt
                          ? new Date(post.publishedAt).toLocaleDateString('en-IN', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'Scheduled'}
                      </span>
                    </div>

                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border capitalize ${
                        post.status === 'published'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}
                    >
                      {post.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-800 leading-relaxed font-medium">{post.content}</p>

                  <div className="flex items-center gap-4 text-[11px] text-slate-500 font-mono pt-1">
                    <span>{post.impressions.toLocaleString('en-IN')} impressions</span>
                    <span>•</span>
                    <span>{post.engagements.toLocaleString('en-IN')} engagements</span>
                    <span>•</span>
                    <span>{post.clicks.toLocaleString('en-IN')} clicks</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: CONTENT CALENDAR */}
      {activeSubTab === 'calendar' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-black text-slate-900">Campaign Schedule Queue</h3>
                <p className="text-xs text-slate-500">Upcoming automated dispatches across platforms.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setComposerStatus('scheduled');
                  setIsComposeOpen(true);
                }}
                className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold flex items-center gap-2 cursor-pointer shadow-xs"
              >
                <Plus className="w-4 h-4" />
                <span>Schedule Post</span>
              </button>
            </div>

            {/* Scheduled Posts Timeline */}
            <div className="space-y-4">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Upcoming Queue ({scheduledQueue.length})
              </h4>
              {scheduledQueue.length === 0 ? (
                <div className="py-12 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
                  No upcoming posts currently scheduled. Click &quot;Schedule Post&quot; to plan one!
                </div>
              ) : (
                scheduledQueue.map((post) => (
                  <div
                    key={post.id}
                    className="p-4 bg-blue-50/50 border border-blue-200/80 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4"
                  >
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-black text-blue-950 font-mono">
                          {post.scheduledAt
                            ? new Date(post.scheduledAt).toLocaleString('en-IN', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })
                            : 'Tomorrow at 10:00 AM'}
                        </span>
                        <div className="flex items-center gap-1 ml-2">
                          {post.platforms.map((p, idx) => (
                            <span key={idx}>{getPlatformIcon(p)}</span>
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-slate-800 font-medium">{post.content}</p>
                    </div>

                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold self-start md:self-center border border-blue-200">
                      Queued for Dispatch
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Recently Published Log */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Recently Published ({publishedQueue.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {publishedQueue.slice(0, 4).map((post) => (
                  <div
                    key={post.id}
                    className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        {post.platforms.map((p, idx) => (
                          <span key={idx}>{getPlatformIcon(p)}</span>
                        ))}
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {post.publishedAt
                          ? new Date(post.publishedAt).toLocaleDateString('en-IN', {
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'Published'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-800 line-clamp-2">{post.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compose Campaign Modal */}
      {isComposeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 max-w-xl w-full shadow-2xl space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-black text-slate-900">Compose Social Campaign</h3>
                <p className="text-xs text-slate-500">
                  Publish or schedule announcements across official channels.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsComposeOpen(false)}
                className="p-1.5 hover:bg-slate-100 rounded-full text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick Templates */}
            <div>
              <span className="text-[11px] font-bold text-slate-500 block mb-1.5">
                Quick Content Presets:
              </span>
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {campaignTemplates.map((t, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setComposerContent(t.text)}
                    className="px-2.5 py-1 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-900 text-slate-700 rounded-lg text-[11px] font-semibold transition-colors whitespace-nowrap cursor-pointer border border-slate-200"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleComposeSubmit} className="space-y-4">
              {/* Target Platforms */}
              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1.5">Target Platforms</label>
                <div className="flex items-center gap-2">
                  {[
                    { id: 'linkedin', name: 'LinkedIn', icon: <Linkedin className="w-4 h-4 text-[#0077B5]" /> },
                    { id: 'x', name: 'X (Twitter)', icon: <Twitter className="w-4 h-4 text-slate-800" /> },
                    { id: 'instagram', name: 'Instagram', icon: <Instagram className="w-4 h-4 text-[#E4405F]" /> },
                    { id: 'youtube', name: 'YouTube', icon: <Youtube className="w-4 h-4 text-[#CD201F]" /> },
                  ].map((p) => {
                    const isSelected = composerPlatforms.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePlatform(p.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-[#0A3D27] text-white border-[#0A3D27] shadow-2xs'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {p.icon}
                        <span>{p.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content Body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-800">Campaign Content *</label>
                  <span className="text-[10px] font-mono text-slate-400">
                    {composerContent.length} chars
                  </span>
                </div>
                <textarea
                  rows={4}
                  required
                  value={composerContent}
                  onChange={(e) => setComposerContent(e.target.value)}
                  placeholder="Draft your announcement here... Include hashtags #IIAB2027 #AgriTech"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                />
              </div>

              {/* Destination URL */}
              <div>
                <label className="text-xs font-bold text-slate-800 block mb-1">CTA Destination Link</label>
                <input
                  type="url"
                  value={composerUrl}
                  onChange={(e) => setComposerUrl(e.target.value)}
                  placeholder="https://iiabexpo.com/floorplan"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                />
              </div>

              {/* Publication Mode */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="text-xs font-bold text-slate-800 block mb-1">Timing</label>
                  <select
                    value={composerStatus}
                    onChange={(e) => setComposerStatus(e.target.value as any)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                  >
                    <option value="published">Publish Immediately</option>
                    <option value="scheduled">Schedule for Later</option>
                    <option value="draft">Save as Draft</option>
                  </select>
                </div>

                {composerStatus === 'scheduled' && (
                  <div>
                    <label className="text-xs font-bold text-slate-800 block mb-1">Schedule Date & Time</label>
                    <input
                      type="datetime-local"
                      value={composerScheduledDate}
                      onChange={(e) => setComposerScheduledDate(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0A3D27]"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsComposeOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createPostMutation.isPending || !composerContent.trim()}
                  className="px-5 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>
                    {createPostMutation.isPending
                      ? 'Dispatching...'
                      : composerStatus === 'scheduled'
                      ? 'Schedule Campaign'
                      : 'Publish Now'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
